import {useEffect, useMemo, useRef, useState} from 'react'
import {Navigate} from 'react-router-dom'
import {Activity, AlertCircle, CalendarDays, CheckCircle2, ChevronRight, KanbanSquare, Layers3, List, LoaderCircle, RefreshCw, Search} from 'lucide-react'
import {PageTitle} from '../../../../../_metronic/layout/core'
import {GetPermissions, useAuth} from '../../../../modules/auth/core/Auth'
import {isPermissionActive} from '../../../permissions/permissionMetadata'
import type {HistoryRecord} from '../../history/data/mockHistory'
import {EMPTY_STATUS_FILTERS, STATUS_PERMISSION, filterStatusRecords, getStatusStage, type StatusFilters, type StatusSort} from '../data/statusBoard'
import {calendarDateKey} from '../data/statusCalendar'
import {fetchBriefingStatus} from '../services/statusApi'
import StatusFiltersPanel from '../components/StatusFiltersPanel'
import StatusKanbanView from '../components/StatusKanbanView'
import StatusListView from '../components/StatusListView'
import StatusCalendarView from '../components/StatusCalendarView'
import StatusRecordDetails from '../components/StatusRecordDetails'
import './status.css'

const REFRESH_INTERVAL = 30000
const VIEW_STORAGE_KEY = 'briefing-promocoes:status:view'
const VIEWS = [
  {id: 'kanban', label: 'Kanban', icon: KanbanSquare, description: 'Acompanhe as promoções pelo progresso do envio ao Pipefy.'},
  {id: 'list', label: 'Lista', icon: List, description: 'Compare etapas, vigências e solicitantes em uma única lista.'},
  {id: 'calendar', label: 'Calendário', icon: CalendarDays, description: 'Explore as promoções pela vigência e veja a agenda de cada dia.'},
] as const
type StatusView = typeof VIEWS[number]['id']

function storedView(): StatusView {
  try {
    const value = localStorage.getItem(VIEW_STORAGE_KEY)
    if (VIEWS.some((view) => view.id === value)) return value as StatusView
  } catch { /* A preferência é opcional quando o armazenamento está indisponível. */ }
  return 'kanban'
}

function StatusBoard() {
  const [records, setRecords] = useState<HistoryRecord[]>([])
  const [filters, setFilters] = useState<StatusFilters>(EMPTY_STATUS_FILTERS)
  const [sort, setSort] = useState<StatusSort>('recent')
  const [view, setView] = useState<StatusView>(storedView)
  const [calendarDate, setCalendarDate] = useState(() => calendarDateKey(new Date()))
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [refreshVersion, setRefreshVersion] = useState(0)
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null)
  const [error, setError] = useState('')
  const [denied, setDenied] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const hasSnapshot = useRef(false)

  useEffect(() => {
    let active = true
    let forbidden = false
    let timer: ReturnType<typeof setTimeout>
    const controller = new AbortController()
    async function load() {
      setRefreshing(true)
      try {
        const data = await fetchBriefingStatus(controller.signal)
        if (!active) return
        setRecords(data)
        hasSnapshot.current = true
        setUpdatedAt(new Date())
        setError('')
      } catch (loadError) {
        if (!active) return
        if (loadError?.response?.status === 403) {
          forbidden = true
          setDenied(true)
        } else {
          setError('Não foi possível atualizar as promoções. Tente novamente.' +
            (hasSnapshot.current ? ' Os últimos dados carregados foram mantidos.' : ''))
        }
      } finally {
        if (active) {
          setLoading(false)
          setRefreshing(false)
          if (!forbidden) timer = setTimeout(() => {
            if (document.visibilityState === 'hidden') {
              const resume = () => {
                if (document.visibilityState === 'visible') {
                  document.removeEventListener('visibilitychange', resume)
                  if (active) setRefreshVersion((value) => value + 1)
                }
              }
              document.addEventListener('visibilitychange', resume, {signal: controller.signal})
            } else {
              setRefreshVersion((value) => value + 1)
            }
          }, REFRESH_INTERVAL)
        }
      }
    }
    load()
    return () => {active = false; controller.abort(); clearTimeout(timer)}
  }, [refreshVersion])

  const visible = useMemo(() => filterStatusRecords(records, filters, sort), [records, filters, sort])
  const sent = visible.filter((record) => getStatusStage(record) === 'sent').length
  const pending = visible.filter((record) => ['queued', 'processing'].includes(getStatusStage(record))).length
  const failed = visible.filter((record) => getStatusStage(record) === 'failed').length
  const sentRate = visible.length ? Math.round(sent / visible.length * 100) : 0
  const currentView = VIEWS.find((item) => item.id === view)!
  const metrics = [
    {label: 'Promoções encontradas', value: visible.length, icon: Layers3, tone: 'blue', note: 'De acordo com os filtros', detail: 'Visão geral', rate: 100},
    {label: 'Em andamento', value: pending, icon: Activity, tone: 'violet', note: 'Na fila ou em processamento', detail: 'Fluxo de envio', rate: visible.length ? pending / visible.length * 100 : 0},
    {label: 'Enviadas ao Pipefy', value: sent, icon: CheckCircle2, tone: 'green', note: `${sentRate}% das promoções filtradas`, detail: 'Envios confirmados', rate: sentRate},
    {label: 'Precisam de atenção', value: failed, icon: AlertCircle, tone: 'red', note: failed ? 'Revise os envios que falharam' : 'Nenhuma falha neste recorte', detail: 'Pontos de atenção', rate: visible.length ? failed / visible.length * 100 : 0},
  ]

  function changeView(next: StatusView) {
    setView(next)
    try {localStorage.setItem(VIEW_STORAGE_KEY, next)} catch { /* Mantém a visualização durante a sessão. */ }
  }

  if (denied) return <Navigate to='/error/404' replace />

  return (
    <div className='briefing-status bs-page'>
      <PageTitle>Status das promoções</PageTitle>
      <header className='bs-hero'>
        <div className='bs-breadcrumb'>Briefing Promoções<ChevronRight size={12} aria-hidden='true' /><span>Status</span></div>
        <div className='bs-hero-content'><div className='bs-heading-main'>
          <span className='bs-heading-icon'><KanbanSquare size={25} aria-hidden='true' /></span>
          <div><h1>Status das promoções</h1>
            <p className='bs-subtitle'>Uma visão clara do envio ao Pipefy e da vigência das promoções.</p></div></div>
          <div className='bs-hero-actions'><span className={`bs-sync${error ? ' is-error' : ''}`}><i />{error ? 'Atualização pendente' : refreshing ? 'Sincronizando dados' : 'Atualização automática'}</span>
            <button type='button' className='bs-button bs-button--primary' onClick={() => setRefreshVersion((value) => value + 1)} disabled={refreshing}>
              <RefreshCw size={16} aria-hidden='true' />{refreshing ? 'Atualizando…' : 'Atualizar'}</button></div>
        </div>
      </header>

      <section className='bs-summary' aria-label='Resumo das promoções filtradas'>
        {metrics.map(({label, value, icon: Icon, tone, note, detail, rate}) => <div className={`bs-metric bs-tone--${tone}`} key={label}>
          <div className='bs-metric-top'><span>{label}</span><span className='bs-summary-icon'><Icon size={19} aria-hidden='true' /></span></div>
          <div className='bs-metric-value'><strong>{loading ? '—' : value.toLocaleString('pt-BR')}</strong><span>{detail}</span></div>
          <div className='bs-metric-bottom'><p>{loading ? 'Carregando informações' : note}</p><div aria-hidden='true'><span style={{width: `${visible.length ? rate : 0}%`}} /></div></div>
        </div>)}
      </section>

      <StatusFiltersPanel records={records} filters={filters} onChange={setFilters} />

      {error && <div className='bs-notice bs-notice--error' role='alert'><AlertCircle size={18} aria-hidden='true' /><span>{error}</span>
        <button type='button' className='bs-link-button' disabled={refreshing} onClick={() => setRefreshVersion((value) => value + 1)}>Tentar novamente</button></div>}

      <section className='bs-workspace' aria-label='Acompanhamento das promoções'>
        <div className='bs-workspace-header'><div className='bs-workspace-title'><h2>Acompanhamento<span>{visible.length}</span></h2>
          <p>{currentView.description}</p></div>
          <div className='bs-view-switch' role='group' aria-label='Modo de visualização'>{VIEWS.map(({id, label, icon: Icon}) =>
            <button type='button' key={id} aria-label={`Visualização em ${label.toLocaleLowerCase('pt-BR')}`} aria-pressed={view === id} onClick={() => changeView(id)}>
              <Icon size={16} aria-hidden='true' /><span>{label}</span></button>)}</div></div>
        <div className='bs-results-meta'><p role='status'>{loading ? 'Carregando promoções…' : `${visible.length} de ${records.length} promoções`}
          {updatedAt && <span><i />Atualizado às {updatedAt.toLocaleTimeString('pt-BR', {hour: '2-digit', minute: '2-digit'})} <span aria-hidden='true'>·</span> A cada 30 s</span>}</p>
          <label className='bs-sort'>Ordenar por<select value={sort} onChange={(event) => setSort(event.target.value as StatusSort)}>
            <option value='recent'>Mais recentes</option><option value='start'>Início da vigência</option><option value='name'>Nome da promoção</option></select></label></div>

        {!loading && !error && !visible.length && <div className='bs-empty' role='status'><span><Search size={27} aria-hidden='true' /></span>
          <strong>{records.length ? 'Nenhuma promoção encontrada' : 'Seu acompanhamento começa aqui'}</strong>
          <p>{records.length ? 'Ajuste os filtros para encontrar o que está procurando.' : 'As promoções aparecerão aqui assim que forem cadastradas.'}</p>
          {records.length > 0 && <button type='button' className='bs-button' onClick={() => setFilters(EMPTY_STATUS_FILTERS)}>Ver todas as promoções</button>}</div>}

        {(loading || visible.length > 0 || view === 'calendar') && <>
          {view === 'kanban' && <StatusKanbanView records={visible} stage={filters.stage} loading={loading} refreshing={refreshing} onOpen={setSelectedId} />}
          {view === 'list' && <StatusListView key={JSON.stringify({filters, sort})} records={visible} loading={loading} onOpen={setSelectedId} />}
          {view === 'calendar' && (loading ? <div className='bs-view-loading' role='status'><LoaderCircle size={22} aria-hidden='true' />Preparando o calendário…</div> :
            <StatusCalendarView records={visible} focusDate={calendarDate} onFocusDateChange={setCalendarDate} onOpen={setSelectedId} />)}
        </>}
      </section>
      <StatusRecordDetails record={records.find((record) => record.id === selectedId) || null} onClose={() => setSelectedId(null)} />
    </div>
  )
}

export default function StatusPage() {
  const permissions = GetPermissions()
  const {permissionsLoaded, permissionsError, refreshPermissions} = useAuth()
  if (!permissionsLoaded) return <p role='status'>Carregando permissões…</p>
  if (permissionsError) return <div role='alert'>Não foi possível validar suas permissões. <button type='button' className='btn btn-light' onClick={() => refreshPermissions(true)}>Tentar novamente</button></div>
  if (!permissions.some((permission) => permission.name === STATUS_PERMISSION && isPermissionActive(permission))) return <Navigate to='/error/404' replace />
  return <StatusBoard />
}
